import { useAnalytics } from '@/contexts/AnalyticsContext';
import { getAdsPlatformIcon } from '@/utils/assets';
import * as Sentry from '@sentry/react';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Plus } from 'iconoir-react';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import ConnectAdsAccountsModal from '../modals/ConnectAdsAccountsModal';
import {
  DEFAULT_FACEBOOK_ADS_SCOPES,
  DEFAULT_GOOGLE_ADS_SCOPES,
  getFacebookUrl,
  getGoogleUrl,
} from '../utils/auth';

export const ConnectAdsAccountButton = () => {
  const { track } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();

  const { createOAuthRequest } = useContext(UserContext);

  const [connectAccountsModalOpen, setConnectAccountsModalOpen] = useState<{
    requestId: string;
    provider: 'google' | 'facebook';
  }>();

  useEffect(() => {
    const googleAdsRequestId = searchParams.get('googleAdsRequestId');
    const facebookAdsRequestId = searchParams.get('facebookAdsRequestId');
    if (googleAdsRequestId) {
      setConnectAccountsModalOpen({
        requestId: googleAdsRequestId,
        provider: 'google',
      });
    } else if (facebookAdsRequestId) {
      setConnectAccountsModalOpen({
        requestId: facebookAdsRequestId,
        provider: 'facebook',
      });
    }
  }, [searchParams]);

  return (
    <>
      <ConnectAdsAccountsModal
        provider={connectAccountsModalOpen?.provider || 'google'}
        requestId={connectAccountsModalOpen?.requestId || ''}
        open={!!connectAccountsModalOpen}
        onClose={() => {
          setSearchParams(new URLSearchParams());
          setConnectAccountsModalOpen(undefined);
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button>
            <Plus className="h-4.5 w-4.5 text-white" />
            Connect
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={async () => {
              track('connect_google_ads_button_click');

              try {
                const from = location.pathname;
                const state = await createOAuthRequest(
                  'google',
                  from,
                  DEFAULT_GOOGLE_ADS_SCOPES,
                );

                window.location.replace(
                  getGoogleUrl(from, state, DEFAULT_GOOGLE_ADS_SCOPES),
                );
              } catch (e) {
                Sentry.captureException(e);
                console.error(e);
              }
            }}
          >
            <img src={getAdsPlatformIcon('google')} className="mr-2 size-5" />
            Connect a Google Ads Account
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              track('connect_meta_ads_button_click');

              try {
                const from = location.pathname;
                const state = await createOAuthRequest(
                  'facebook',
                  from,
                  DEFAULT_FACEBOOK_ADS_SCOPES,
                );

                window.location.replace(
                  getFacebookUrl(from, state, DEFAULT_FACEBOOK_ADS_SCOPES),
                );
              } catch (e) {
                Sentry.captureException(e);
                console.error(e);
              }
            }}
          >
            <img src={getAdsPlatformIcon('facebook')} className="mr-2 size-5" />
            Connect a Meta Ads Account
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <img src={getAdsPlatformIcon('taboola')} className="mr-2 size-5" />
            <span>Taboola</span>
            <span className="ml-auto text-xs text-gray-400">Coming Soon</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <img src={getAdsPlatformIcon('linkedin')} className="mr-2 size-5" />
            <span>LinkedIn Ads</span>
            <span className="ml-auto text-xs text-gray-400">Coming Soon</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <img src={getAdsPlatformIcon('tiktok')} className="mr-2 size-5" />
            <span>TikTok Ads</span>
            <span className="ml-auto text-xs text-gray-400">Coming Soon</span>
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <img src={getAdsPlatformIcon('bing')} className="mr-2 size-5" />
            <span>Bing Ads</span>
            <span className="ml-auto text-xs text-gray-400">Coming Soon</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
